// CSV export of the Stores list (WEB_PLAN.md §5.5 "Export CSV"): built in the browser from the rows on screen, in the
// order and with the columns on screen, so what a manager downloads is exactly what they were looking at.
//
// - RFC 4180: fields with a comma, quote, CR or LF (or edge spaces, which some readers trim) are quoted and inner
//   quotes doubled; lines end with CRLF.
// - A UTF-8 BOM first, or Excel on Windows reads "Café" as "CafÃ©".
// - Text that starts like a formula (= + - @, tab, CR) gets a leading apostrophe: store names, notes and tags are
//   typed by reps, and a spreadsheet would otherwise run "=HYPERLINK(...)" from a cell (CSV injection). Numbers are
//   written as numbers and never touched, so "-5" stays a number.

export type CsvValue = string | number | null | undefined;

const NEEDS_QUOTES = /[",\r\n]|^\s|\s$/;
const FORMULA_START = /^[=+\-@\t\r]/;

/** One field: numbers as plain digits (no grouping, nothing for NaN), text guarded and quoted when needed. */
export function csvField(value: CsvValue): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  const text = FORMULA_START.test(value) ? `'${value}` : value;
  return NEEDS_QUOTES.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** A whole file: BOM, the header row, then one CRLF-terminated line per row. */
export function toCsv(header: readonly string[], rows: readonly (readonly CsvValue[])[]): string {
  const lines = [header.map(csvField).join(',')];
  for (const row of rows) lines.push(row.map(csvField).join(','));
  return `﻿${lines.join('\r\n')}\r\n`;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** "2026-09-22" in local time (date-only custom fields). */
export function csvDate(date: Date | null | undefined): string {
  if (!date || Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** "2026-09-22 14:43" in local time: sorts as text and every spreadsheet reads it as a date. */
export function csvDateTime(date: Date | null | undefined): string {
  if (!date || Number.isNaN(date.getTime())) return '';
  return `${csvDate(date)} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** "acme-roofing-stores-2026-10-01.csv" (company name slugged; just "stores-…" without one). */
export function csvFileName(companyName: string | null | undefined, now: Date = new Date()): string {
  const slug = (companyName ?? '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/g, '');
  return `${slug === '' ? '' : `${slug}-`}stores-${csvDate(now)}.csv`;
}
